/**
 * PostEditor.tsx
 *
 * Plain text editor for social media post composition.
 * Built with Lexical framework using PlainTextPlugin — Enter creates
 * LineBreakNodes (\n), not paragraph blocks. This gives perfect round-trip
 * fidelity: what the user types is exactly what gets stored and published.
 *
 * Features:
 * - Hashtag auto-styling (#hashtag)
 * - Link auto-detection
 * - Emoji picker
 * - Character counter with platform limits
 * - Auto-focus on mount
 * - Keyboard shortcuts
 * - Accessible (WCAG 2.1 AA)
 *
 * Usage:
 * ```tsx
 * <PostEditor
 *   value={content}
 *   onChange={(text) => setContent(text)}
 *   platforms={['linkedin', 'twitter']}
 *   placeholder="What's on your mind?"
 * />
 * ```
 */

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $createParagraphNode, $createTextNode, $createLineBreakNode, type EditorState } from 'lexical';
import { AutoLinkNode } from '@lexical/link';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HashtagNode, HashtagPlugin } from './lexical/HashtagPlugin';
import { CharacterCountPlugin } from './lexical/CharacterCountPlugin';
import { EditorToolbar } from './lexical/EditorToolbar';
import { EmojiShortcodePlugin } from './lexical/EmojiShortcodePlugin';
import { devLog } from '@/utils/logger';
import { Tooltip } from '@/components/ui/Tooltip';
import { reportClientFreeze } from '@/services/telemetry';
import { useComposerStore } from '@/stores/composerStore';
import { useFreezeDetector, type FreezeDetectorContext } from '@/hooks/useFreezeDetector';
import { startRollingProfiler, stopProfiler, captureHotStack } from '@/utils/freezeProfiler';

/**
 * Long tasks blocking the main thread for longer than this (ms) are
 * reported as a potential freeze. 2000ms is well above normal input-latency
 * long tasks and targets genuine UI-blocking freezes.
 */
const LONGTASK_FREEZE_THRESHOLD_MS = 2000;

/**
 * Main-thread heartbeat interval (ms) for the freeze-recovery detector
 * below. Kept short enough to bound the eventual `blockedMs` measurement
 * error, long enough to be negligible overhead.
 */
const HEARTBEAT_INTERVAL_MS = 1000;

/**
 * A heartbeat tick that arrives this many ms LATER than expected (beyond
 * `HEARTBEAT_INTERVAL_MS`) means the main thread was just blocked for
 * roughly that long and has just recovered. 3000ms comfortably excludes
 * ordinary scheduling jitter/GC pauses while still catching the genuine
 * multi-second hard freezes this detector exists to diagnose.
 */
const FREEZE_RECOVERY_THRESHOLD_MS = 3000;

/**
 * Narrow, non-standard `performance.memory` (Chrome/Blink only). Not present
 * in TypeScript's lib.dom.d.ts performance typings. Mirrors the identical
 * narrowing in `src/services/telemetry.ts` (not exported from there, so
 * duplicated here for the freeze-detector heartbeat context).
 */
interface PerformanceWithMemory extends Performance {
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
}

const getJsHeapMB = (): number | undefined => {
  try {
    const perf = performance as PerformanceWithMemory;
    if (perf.memory && typeof perf.memory.usedJSHeapSize === 'number') {
      return Math.round(perf.memory.usedJSHeapSize / 1048576);
    }
    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Auto-link matchers for URL detection
 */
const URL_MATCHER =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const EMAIL_MATCHER =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

const MATCHERS = [
  (text: string) => {
    const match = URL_MATCHER.exec(text);
    if (match === null) {
      return null;
    }
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
    };
  },
  (text: string) => {
    const match = EMAIL_MATCHER.exec(text);
    if (match === null) {
      return null;
    }
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: `mailto:${fullMatch}`,
    };
  },
];

/**
 * Lexical editor theme configuration
 */
const editorTheme = {
  link: 'text-info hover:underline cursor-pointer',
};

interface PostEditorProps {
  /**
   * Initial content (plain text)
   */
  value?: string;

  /**
   * Callback when content changes (plain text)
   */
  onChange?: (content: string) => void;

  /**
   * Callback when editor state changes (Lexical EditorState)
   */
  onEditorChange?: (editorState: EditorState) => void;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Maximum character length
   */
  maxLength?: number;

  /**
   * Selected platforms (for character counting)
   */
  platforms?: string[];

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Custom class name
   */
  className?: string;

  /**
   * Auto-focus on mount
   */
  autoFocus?: boolean;

  /**
   * Compact mode (shorter min height for modal usage)
   */
  compact?: boolean;

  /**
   * Fill height mode: the editor content area grows to fill available space (flex-1).
   * Use inside a flex column container that constrains its height.
   */
  fillHeight?: boolean;

  /**
   * Selected channel IDs for AI context
   */
  selectedChannels?: number[];

  /**
   * Callback when AI inserts an image (from Unsplash)
   */
  onInsertImage?: (url: string, credit: string, downloadUrl: string) => void;
}

/**
 * Set editor content from a plain text string.
 *
 * With PlainTextPlugin, the editor uses a single ParagraphNode containing
 * TextNodes and LineBreakNodes. Each \n in the input becomes a LineBreakNode.
 * getTextContent() returns the exact same string back — perfect round-trip.
 */
function setEditorContentFromString(root: ReturnType<typeof $getRoot>, text: string): void {
  root.clear();

  if (!text) {
    return;
  }

  const paragraph = $createParagraphNode();
  const lines = text.split('\n');

  lines.forEach((line, i) => {
    if (i > 0) {
      paragraph.append($createLineBreakNode());
    }
    if (line) {
      paragraph.append($createTextNode(line));
    }
  });

  root.append(paragraph);
}

/**
 * Plugin to sync external value changes to editor.
 * Handles bidirectional sync: external changes → editor.
 */
function ExternalValuePlugin({ value }: { value?: string }): null {
  const [editor] = useLexicalComposerContext();
  const lastValueRef = useRef<string | undefined>(undefined);
  const isFirstMount = useRef(true);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      if (value) {
        editor.update(() => {
          const root = $getRoot();
          setEditorContentFromString(root, value);
          root.selectStart();
        });
        lastValueRef.current = value;
      }
      return;
    }

    // Skip if value hasn't changed
    if (value === lastValueRef.current) return;

    editor.update(() => {
      const root = $getRoot();
      const currentText = root.getTextContent();

      // Only update if external value differs from editor content
      // This prevents infinite loops from user typing
      if (value !== currentText) {
        setEditorContentFromString(root, value || '');
      }
    });

    lastValueRef.current = value;
  }, [editor, value]);

  return null;
}

/**
 * Custom auto-focus plugin that focuses the editor and scrolls to the top.
 * Replaces Lexical's AutoFocusPlugin which places cursor at end.
 */
/**
 * `initialConfig.editable` is only read at mount. The editor is often mounted while `disabled` is true (busy,
 * permissions or the fresh post still loading) and becomes editable later, so keep Lexical in sync.
 */
function EditablePlugin({ disabled }: { disabled: boolean }): null {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (editor.isEditable() !== !disabled) editor.setEditable(!disabled);
  }, [editor, disabled]);
  return null;
}

function AutoFocusStartPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // defaultSelection: 'rootStart' places cursor at beginning automatically
    editor.focus(undefined, { defaultSelection: 'rootStart' });

    // Scroll to top after focus settles
    requestAnimationFrame(() => {
      const el = editor.getRootElement();
      if (el) {
        el.scrollTop = 0;
      }
    });
  }, [editor]);

  return null;
}

/**
 * PostEditor component
 * Plain text editor for post composition
 */
export function PostEditor({
  value = '',
  onChange,
  onEditorChange,
  placeholder,
  maxLength,
  platforms = [],
  disabled = false,
  className = '',
  autoFocus = true,
  compact = false,
  fillHeight = false,
  selectedChannels = [],
  onInsertImage,
}: PostEditorProps): JSX.Element {
  const { t } = useTranslation('posts');
  const { t: tc } = useTranslation('common');
  const [showToolbar, setShowToolbar] = useState(true);

  /**
   * Telemetry-only: mirrors the latest `value` prop so the long-task
   * observer and the Lexical `onError` callback (both set up once, see
   * below) can read the current caption length without becoming stale or
   * forcing the editor to remount.
   */
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  /**
   * Telemetry: off-main-thread hard-freeze detector. Unlike the
   * `PerformanceObserver('longtask')` below (which can only report AFTER a
   * blocking task finishes), this runs a dedicated Web Worker that
   * heartbeats with the main thread and can self-report a freeze WHILE the
   * main thread is still wedged — the only way to ever see telemetry for a
   * hang the user has to force-kill the tab over. Purely observational;
   * see `useFreezeDetector` for the full worker/heartbeat contract.
   */
  useFreezeDetector((): FreezeDetectorContext => ({
    route: window.location.pathname,
    captionLength: valueRef.current?.length ?? 0,
    mediaCount: useComposerStore.getState().media.length,
    jsHeapMB: getJsHeapMB(),
    userAgent: navigator.userAgent?.slice(0, 400),
  }));

  /**
   * Telemetry: continuous JS Self-Profiling (Chromium only, requires the
   * `Document-Policy: js-profiling` response header — see vite.config.ts /
   * prod nginx). Runs a background sampling profiler for the lifetime of
   * the composer so that WHEN a long task is detected below, we can pull
   * the EXACT hot call-stack (function/file:line) the main thread was
   * pinned in DURING the freeze — the profiler samples the stack even
   * while the thread is blocked, which `PerformanceObserver('longtask')`
   * alone can never tell us (it only reports duration, after the fact).
   * Fully inert (no-op) in unsupported browsers or if the header is
   * missing; see `src/utils/freezeProfiler.ts` — never throws.
   */
  useEffect(() => {
    startRollingProfiler();
    return () => {
      stopProfiler();
    };
  }, []);

  /**
   * Telemetry: observe main-thread long tasks while the editor is mounted.
   * A long task exceeding LONGTASK_FREEZE_THRESHOLD_MS likely corresponds
   * to a perceptible freeze; report it so we can confirm on real devices
   * whether a fix worked. Passive/observational only — never touches
   * editor state or behavior. Guarded for browsers without `longtask`
   * PerformanceObserver support (e.g. Safari/Firefox), and cleaned up on
   * unmount.
   */
  useEffect(() => {
    let observer: PerformanceObserver | undefined;

    try {
      observer = new PerformanceObserver((list) => {
        try {
          for (const entry of list.getEntries()) {
            if (entry.duration > LONGTASK_FREEZE_THRESHOLD_MS) {
              const longtaskMs = Math.round(entry.duration);
              const captionLength = valueRef.current?.length ?? 0;
              const mediaCount = useComposerStore.getState().media.length;

              // Pull the hottest call-stack from the rolling profiler's
              // just-stopped trace (its samples span the duration of the
              // long task we just detected) before reporting. Fire-and-
              // forget: PerformanceObserver callbacks can't be async, and
              // this must never delay or block on the report.
              void captureHotStack()
                .then((profileTop) => {
                  reportClientFreeze({
                    kind: 'longtask',
                    longtaskMs,
                    captionLength,
                    mediaCount,
                    profileTop: profileTop ?? undefined,
                  });
                })
                .catch(() => {
                  // captureHotStack() is fully self-guarded and should
                  // never reject, but report without a profile as a
                  // last-resort fallback rather than lose the event.
                  reportClientFreeze({ kind: 'longtask', longtaskMs, captionLength, mediaCount });
                });
            }
          }
        } catch {
          // Telemetry must never disrupt the editor; swallow and move on.
        }
      });
      observer.observe({ entryTypes: ['longtask'] });
    } catch {
      // 'longtask' entries unsupported in this browser — no-op.
    }

    return () => {
      try {
        observer?.disconnect();
      } catch {
        // no-op
      }
    };
  }, []);

  /**
   * Telemetry: MAIN-THREAD freeze-RECOVERY detector (self-liveness
   * heartbeat). Production data shows this is the reliable path: 14/14 real
   * freeze reports came from the off-thread `useFreezeDetector` worker
   * above (`kind: 'freeze-detected'`, no stack) and ZERO came from the
   * `PerformanceObserver('longtask')` handler — the real freezes are
   * continuous 40-64s main-thread blocks that starve/coalesce the longtask
   * observer's callback, so we capture "it froze" but never the culprit
   * stack.
   *
   * A plain `setInterval` callback is the most basic main-thread liveness
   * primitive there is: it can only run once the main thread is free, so
   * the tick immediately AFTER a long block fires as soon as the thread
   * recovers, independent of whatever is happening to the longtask
   * observer. Each tick compares `performance.now()` to the previous
   * tick's timestamp; a gap that exceeds `FREEZE_RECOVERY_THRESHOLD_MS`
   * beyond the expected `HEARTBEAT_INTERVAL_MS` means the main thread was
   * just blocked for roughly that long and has JUST come back. We then
   * pull the hot stack from the SAME rolling profiler the longtask
   * observer uses (`captureHotStack()` — its samples span the block we
   * just detected, since it was sampling the whole time we were blocked)
   * and report it under `kind: 'longtask'` (semantically correct — it IS a
   * main-thread block, just detected via recovery rather than the
   * PerformanceObserver — and needs no backend schema change).
   *
   * The existing longtask observer and off-thread worker both stay in
   * place (belt-and-suspenders); this is a third, independent path that
   * reliably fires for the freezes that actually starve the other two.
   *
   * Backgrounded-tab guard: browsers throttle a hidden tab's `setInterval`
   * to ~1/min, which would otherwise make EVERY tick after backgrounding
   * look like a ~60s "freeze recovery" — a false positive, not a real
   * main-thread block. We still advance `lastTick` on every tick (even
   * while hidden) so the timestamp always reflects "now", but only
   * capture/report when `document.hidden` is false, i.e. the block was
   * both real AND on a visible tab.
   *
   * Hidden→visible transition race: the in-callback `document.hidden` check
   * alone is not enough. Once a tab has been hidden long enough to be
   * throttled, Chrome fixes the next tick's fire time at throttle time
   * (~1/min). If the user returns to the tab a few seconds before that
   * already-scheduled throttled tick fires, `document.hidden` reads `false`
   * by the time the callback runs — so the guard above doesn't catch it —
   * even though `gap` still reflects the full throttled interval, producing
   * a spurious ~60s "freeze". A `visibilitychange` listener closes this gap
   * by rebaselining `lastTick` at the actual moment the tab becomes visible,
   * so the first tick afterward measures the gap from the real un-hide
   * instant instead of from the last throttled tick.
   */
  useEffect(() => {
    let lastTick = performance.now();

    const onVisibilityChange = () => {
      if (!document.hidden) {
        lastTick = performance.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const intervalId = window.setInterval(() => {
      try {
        const now = performance.now();
        const gap = now - lastTick;
        // Reset immediately (before any check) so consecutive freezes —
        // Sara's repro pattern is roughly one per minute — are each
        // measured independently instead of the gap accumulating across
        // ticks once the thread starts freezing repeatedly, and so the
        // first tick after a tab is un-hidden doesn't inherit a stale
        // (throttled) gap and fire a spurious recovery report.
        lastTick = now;

        if (document.hidden) {
          // Tab was backgrounded during this interval — a large gap here
          // is browser throttling, not a real main-thread block. Skip
          // silently; the timestamp reset above still keeps us honest for
          // the next visible tick.
          return;
        }

        const overage = gap - HEARTBEAT_INTERVAL_MS;
        if (overage <= FREEZE_RECOVERY_THRESHOLD_MS) {
          return;
        }

        const blockedMs = Math.round(gap);
        const captionLength = valueRef.current?.length ?? 0;
        const mediaCount = useComposerStore.getState().media.length;

        // Fire-and-forget, same pattern as the longtask observer above:
        // captureHotStack() stops the rolling profiler (whose samples
        // cover the block we were just wedged in), extracts the hottest
        // frames, and restarts it so the NEXT freeze is profiled too.
        void captureHotStack()
          .then((profileTop) => {
            reportClientFreeze({
              kind: 'longtask',
              longtaskMs: blockedMs,
              captionLength,
              mediaCount,
              profileTop: profileTop ?? undefined,
            });
          })
          .catch(() => {
            // captureHotStack() is fully self-guarded and should never
            // reject, but report without a profile as a last-resort
            // fallback rather than lose the event.
            reportClientFreeze({ kind: 'longtask', longtaskMs: blockedMs, captionLength, mediaCount });
          });
      } catch {
        // Telemetry must never disrupt the editor; swallow and move on.
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  /**
   * Handle editor state changes
   */
  const handleChange = (editorState: EditorState) => {
    if (onEditorChange) {
      onEditorChange(editorState);
    }

    editorState.read(() => {
      const root = $getRoot();
      const text = root.getTextContent();

      if (onChange) {
        onChange(text);
      }
    });
  };

  /**
   * Initial editor configuration
   */
  const initialConfig = {
    namespace: 'PostEditor',
    theme: editorTheme,
    nodes: [HashtagNode, AutoLinkNode],
    onError: (error: Error) => {
      devLog.error('Lexical error:', error);
      // Telemetry: captures Lexical's update-recursion guard error (and any
      // other editor error) so a freeze/crash can be confirmed and its
      // stack inspected on the reporting user's real device.
      reportClientFreeze({
        kind: 'editor-error',
        errorName: error.name,
        errorMessage: error.message?.slice(0, 500),
        errorStack: error.stack?.slice(0, 4000),
        captionLength: valueRef.current?.length ?? 0,
        mediaCount: useComposerStore.getState().media.length,
      });
    },
    editable: !disabled,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={`relative border border-gray-200 rounded-lg bg-white overflow-hidden focus-within:border-gray-400 transition-shadow min-w-0 ${fillHeight ? 'lg:flex lg:flex-col lg:min-h-0 lg:h-full' : `flex flex-col resize-y ${compact ? 'h-[clamp(160px,28vh,320px)]' : 'h-[320px]'} min-h-[160px] max-h-[70vh]`} ${className}`}>
        {/* Toolbar toggle button - top right */}
        <Tooltip content={showToolbar ? tc('hide_toolbar') : tc('show_toolbar')} side="left" sideOffset={4}>
          <button
            type="button"
            onClick={() => setShowToolbar(prev => !prev)}
            className="absolute top-2 right-2 z-10 p-1 rounded max-sm:before:absolute max-sm:before:-inset-[10px] max-sm:before:content-[''] text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label={showToolbar ? tc('hide_toolbar') : tc('show_toolbar')}
          >
            <span
              className="material-symbols-outlined text-[18px] leading-none inline-block"
            >
              {showToolbar ? 'expand_less' : 'expand_more'}
            </span>
          </button>
        </Tooltip>

        {/* Toolbar - collapsible with smooth animation */}
        <div
          className="transition-all duration-300 ease-in-out overflow-hidden"
          style={{
            maxHeight: showToolbar ? 'clamp(120px, 20vh, 200px)' : '0px',
            opacity: showToolbar ? 1 : 0,
          }}
        >
          <EditorToolbar
            caption={value}
            onCaptionGenerated={onChange}
            selectedChannels={selectedChannels}
            onInsertImage={onInsertImage}
            platforms={platforms}
          />
        </div>

        {/* Editor */}
        <div className={`relative ${fillHeight ? 'lg:flex-1 lg:min-h-0 lg:flex lg:flex-col' : 'flex-1 min-h-0 flex flex-col'}`}>
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className={`flex-1 min-h-0 h-full ${fillHeight ? 'lg:!h-full lg:flex-1 lg:min-h-[200px]' : ''} overflow-y-auto p-4 outline-none font-sans text-[16px] lg:text-base text-gray-900 whitespace-pre-wrap break-words ${
                  disabled ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                aria-label={t('post_editor_aria_label')}
                aria-disabled={disabled}
              />
            }
            placeholder={
              <div className="absolute top-4 left-4 text-gray-400 pointer-events-none select-none">
                {placeholder || t('post_editor_placeholder')}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>

        {/* Character Counter - desktop only when no platforms selected (toolbar has compact version when platforms selected) */}
        {platforms.length === 0 && (
          <div className="px-4 pb-3 hidden sm:block">
            <CharacterCountPlugin
              platforms={platforms}
              maxLength={maxLength}
            />
          </div>
        )}

        {/* Plugins */}
        <HistoryPlugin />
        <AutoLinkPlugin matchers={MATCHERS} />
        <HashtagPlugin />
        <EmojiShortcodePlugin />
        <OnChangePlugin onChange={handleChange} />
        <ExternalValuePlugin value={value} />
        <EditablePlugin disabled={!!disabled} />
        {autoFocus && <AutoFocusStartPlugin />}
      </div>
    </LexicalComposer>
  );
}

export default PostEditor;
